import { createLogger } from "@/lib/logger";
import { isValidTimezone, time } from "@/lib/time";
import { trimToUndefined } from "@/lib/utils/string";
import type { HintChargeItem } from "./types/charges";

const logger = createLogger("hint/charge-items");

export interface HintChargeItemReference {
  id: string;
  name?: string;
}

interface BuildHintChargePayloadParams {
  chargeItem: HintChargeItem;
  priceInCents: number;
  quantity: number;
  /**
   * The date the service happens, `YYYY-MM-DD`, from {@link formatHintServiceDate}. Hint calls
   * this `date` on create and surfaces it as `service_date` on the charge; without it Hint dates
   * the charge the day it was raised, so a visit booked today for next Tuesday bills as today.
   */
  serviceDate?: string;
}

interface FormatHintServiceDateParams {
  /** Appointment start, ISO 8601. */
  startIso: string | undefined;
  /** The practice's IANA timezone — the calendar day the visit falls on for the people billing it. */
  practiceTimezone: string | undefined;
}

/**
 * Renders an appointment start as the calendar date Hint should bill it under.
 *
 * Returns `undefined` rather than guessing when the practice timezone is missing or invalid.
 * Falling back to UTC would silently shift an evening visit to the next day, and a service date
 * that is confidently wrong on a billing document is worse than one Hint fills in itself — that
 * default is at least recognisable as the date the charge was raised.
 */
export function formatHintServiceDate({
  startIso,
  practiceTimezone,
}: FormatHintServiceDateParams): string | undefined {
  if (!startIso) {
    return undefined;
  }

  if (!practiceTimezone || !isValidTimezone(practiceTimezone)) {
    logger.warn("Cannot date a Hint charge without a valid practice timezone", {
      practiceTimezone: practiceTimezone ?? null,
    });
    return undefined;
  }

  const start = time.utc(startIso);
  if (!start.isValid()) {
    return undefined;
  }

  return start.tz(practiceTimezone).format("YYYY-MM-DD");
}

interface DeduplicateHintChargeItemReferencesParams {
  chargeItems: Array<HintChargeItemReference | string>;
  missingIdErrorMessage: string;
}

interface MatchHintChargeItemsToReferencesParams {
  references: HintChargeItemReference[];
  chargeItems: HintChargeItem[];
}

/**
 * Deduplicates stored Hint charge item references while preserving any discovered display name.
 */
export function deduplicateHintChargeItemReferences({
  chargeItems,
  missingIdErrorMessage,
}: DeduplicateHintChargeItemReferencesParams): HintChargeItemReference[] {
  const deduplicatedItems = new Map<string, HintChargeItemReference>();

  chargeItems.forEach((item) => {
    const chargeItemId = typeof item === "string" ? item : item.id;
    if (!chargeItemId) {
      throw new Error(missingIdErrorMessage);
    }

    const name = typeof item === "string" ? undefined : item.name;
    const existingItem = deduplicatedItems.get(chargeItemId);

    if (!existingItem) {
      deduplicatedItems.set(chargeItemId, { id: chargeItemId, name });
      return;
    }

    if (!existingItem.name && name) {
      deduplicatedItems.set(chargeItemId, { id: chargeItemId, name });
    }
  });

  return Array.from(deduplicatedItems.values());
}

/**
 * Re-applies the stored reference order after fetching the matching Hint charge items by ID.
 */
export function matchHintChargeItemsToReferences({
  references,
  chargeItems,
}: MatchHintChargeItemsToReferencesParams): HintChargeItem[] {
  const chargeItemMap = new Map(chargeItems.map((chargeItem) => [chargeItem.id, chargeItem]));

  return references
    .map((reference) => chargeItemMap.get(reference.id))
    .filter((chargeItem): chargeItem is HintChargeItem => chargeItem !== undefined);
}

/**
 * Builds the sanitized Hint charge payload shared by booking and manual charge flows.
 */
export function buildHintChargePayload({
  chargeItem,
  priceInCents,
  quantity,
  serviceDate,
}: BuildHintChargePayloadParams) {
  return {
    charge_item: {
      id: chargeItem.id,
      name: chargeItem.name,
      code: trimToUndefined(chargeItem.code),
      code_type: trimToUndefined(chargeItem.code_type),
    },
    price_in_cents: priceInCents,
    quantity,
    category: chargeItem.category?.id ? { id: chargeItem.category.id } : undefined,
    // Omitted rather than sent null so Hint keeps its own default when we can't date the visit.
    ...(serviceDate ? { date: serviceDate } : {}),
  };
}
